import React, { useEffect, useState } from 'react';
import { Router } from 'react-router-dom';
import TagManager from 'react-gtm-module';
import { Helmet } from 'react-helmet-async';
import i18next from 'i18next';
import { Toaster } from 'sonner';
import { useDispatch, useSelector } from 'react-redux';
import isEmpty from 'lodash.isempty';
import jwt from 'jsonwebtoken';
import history from './store/utils/history';
import useInitHook from './util/hooks/useInitHook';
import './workers';
import Footer from './components/Footer/Footer';
import Header from './components/Header/Header';
import AppRoutes from './AppRoutes';
import useRefreshHook from './util/hooks/useRefreshHook';
import RefreshModal from './components/Modals/RefreshModal/RefreshModal';
import {
  getCurrentAccount,
  selectAgreementTypesToReSign,
} from './store/selectors/accountSelectors';
import NoPermission from './components/NoPermission/NoPermission';
import ScrollToTopRouter from './components/ScrollToTopRouter/ScrollToTopRouter';
import MaintenancePage from './pages/Maintenance/MaintenancePage';
import { useGetIsImpersonating } from './util/hooks/useGetIsImpersonating';
import { useBrokerName } from './util/hooks/userBrokerName';
import GlobalStyles from './assets/styles/base';
import { PageLayout } from './components/PageLayout/PageLayout';
import loadBranchScript from './branchScript';
import ErrorBoundary from './components/ErrorBoundary';
import { IdleTimeContext } from './providers/IdleTimeContextProvider';
import TermsModal from './components/Modals/UpdateTerms/TermsModal';
import config from './config';
import {
  hideAgreementModalPages,
  termsArrayHelper,
} from './util/helpers/termsHelper';
import { useBrokerCode } from './util/hooks/useBrokerCode';
import { fetchAccountSettings } from './store/actions/account/accountActions';
import { authScopeStringGetHelper } from './util/helpers/authScopeHelpers';
import { JWT_TOKEN } from './constants/localStorage';
import {
  INSIDER_PLUS_PAGE,
  INSIDER_PLUS_MEMBERSHIP_PAGE,
  INSIDER_PLUS_FORUM_AUTH_PAGE,
} from './constants/pages';

const App = () => {
  const { isImpersonating } = useGetIsImpersonating();
  const currentAccount = useSelector(getCurrentAccount);
  const [sessionAlert, setSessionAlert] = useState(false);
  const [accountCode, setAccountCode] = useState('');
  const [noPermissionsModal, setNoPermissionsModal] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [termsArray, setTermsArray] = useState([]);
  const [isPrimary, setIsPrimary] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [userWithMultipleAccounts, setUserWithMultipleAccounts] = useState(
    false,
  );
  const agreementTypes = useSelector(selectAgreementTypesToReSign);
  const dispatch = useDispatch();

  const maintenance = false;

  const { brokerTheme } = config;
  const brokerName = useBrokerName();
  const brokerCode = useBrokerCode();
  useInitHook(
    setNoPermissionsModal,
    setFirstName,
    setLastName,
    setUserWithMultipleAccounts,
    maintenance,
  );
  const getRemainingTime = useRefreshHook(
    setSessionAlert,
    sessionAlert,
    history,
  );

  // Seed the brand into the dataLayer before gtm.js evaluates so GTM can gate
  // tags by brand explicitly. Necessary in shared lower-env containers where
  // both brands load the same container and are otherwise only separable by
  // hostname; harmless in prod (each brand loads its own container). Static
  // build-time value, set once — additive and idempotent.
  const tagManagerArgs = {
    gtmId: config.gtm.gtmId,
    auth: config.gtm.auth,
    preview: config.gtm.preview,
    dataLayer: {
      brokerCode: config.brokerCode,
    },
  };

  const closeTermsModal = () => {
    setIsTermsModalOpen(false);
  };

  useEffect(() => {
    if (maintenance) {
      return;
    }

    TagManager.initialize(tagManagerArgs);
    if (brokerTheme !== 'LegacyPM') {
      loadBranchScript();
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!isEmpty(agreementTypes)) {
      const jwtToken = authScopeStringGetHelper(JWT_TOKEN);
      const decodedTokenInfo = jwt.decode(jwtToken);
      setIsPrimary(decodedTokenInfo?.IsPrimaryUser === 'True');
      const terms = termsArrayHelper(agreementTypes);
      setTermsArray(terms);
      setIsTermsModalOpen(true);
    }
  }, [agreementTypes]);

  useEffect(() => {
    if (maintenance) {
      return;
    }

    setAccountCode(currentAccount.AccountCode);
  }, [currentAccount, maintenance]);

  useEffect(() => {
    sessionStorage.removeItem('comingSoonClosed');
  }, []);

  const onAgreementsAccepted = () => {
    closeTermsModal();
    dispatch(
      fetchAccountSettings({
        accountUid: currentAccount?.AccountUid,
      }),
    );
  };

  const [currentPath, setCurrentPath] = useState(history.location.pathname);

  useEffect(() => {
    const unlisten = history.listen((location) => {
      setCurrentPath(location.pathname);
    });
    return () => {
      unlisten();
    };
    // history is the singleton from store/utils/history — stable across renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // [Insider+] Footer is suppressed on the iframe-hosting routes so the
  // embedded portal can manage its own chrome. INSIDER_PLUS_PAGE is
  // prefix-matched (not exact) since it now covers deep module URLs too,
  // e.g. /insider-plus/metals-playbook.
  const hideFooter =
    currentPath === INSIDER_PLUS_PAGE ||
    currentPath.startsWith(`${INSIDER_PLUS_PAGE}/`) ||
    [INSIDER_PLUS_MEMBERSHIP_PAGE, INSIDER_PLUS_FORUM_AUTH_PAGE].includes(
      currentPath,
    );

  return (
    <>
      <Router history={history}>
        <Toaster
          position="top-right"
          closeButton
          toastOptions={{
            duration: 3000,
          }}
        />
        <GlobalStyles />
        <Helmet>
          <title>
            {!isImpersonating
              ? i18next.t('app.title', { brokerName })
              : i18next.t('app.impersonatingTitle', {
                  accountCode,
                  brokerName,
                })}
          </title>
        </Helmet>
        <Header
          setNoPermissionsModal={setNoPermissionsModal}
          maintenance={maintenance}
        />
        {maintenance ? (
          <PageLayout>
            <MaintenancePage />
          </PageLayout>
        ) : (
          <>
            <IdleTimeContext.Provider value={getRemainingTime}>
              <ScrollToTopRouter />
              <PageLayout>
                <NoPermission isOpen={noPermissionsModal}>
                  <ErrorBoundary>
                    <AppRoutes />
                  </ErrorBoundary>
                </NoPermission>
              </PageLayout>
            </IdleTimeContext.Provider>
            {brokerName === 'GoldSilver' &&
              brokerCode === 'GS' &&
              !hideAgreementModalPages.includes(history?.location?.pathname) &&
              isTermsModalOpen && (
                <TermsModal
                  isPrimary={isPrimary}
                  primaryUserFirstName={firstName}
                  primaryUserLastName={lastName}
                  isModalOpen={isTermsModalOpen}
                  closeModal={closeTermsModal}
                  termsArray={termsArray}
                  onSuccess={onAgreementsAccepted}
                  isImpersonating={isImpersonating}
                  userWithMultipleAccounts={userWithMultipleAccounts}
                />
              )}
          </>
        )}
        {!hideFooter && <Footer history={history} />}
      </Router>
      <RefreshModal isOpen={sessionAlert} close={setSessionAlert} />
    </>
  );
};

export default App;
